import { MAP_MARKERS_SVG_ICONS_BASE } from "libraries/svgPaths";

export type PlaceIconCategory =
  | "city"
  | "airport"
  | "hotel"
  | "food"
  | "museum"
  | "park"
  | "shopping"
  | "transit"
  | "fuel"
  | "parking"
  | "hospital"
  | "education"
  | "attraction"
  | "default";

const PLACE_ICON_CATEGORY_CODES: Record<PlaceIconCategory, string> = {
  city: "c",
  airport: "a",
  hotel: "h",
  food: "f",
  museum: "m",
  park: "p",
  shopping: "s",
  transit: "t",
  fuel: "g",
  parking: "k",
  hospital: "x",
  education: "e",
  attraction: "r",
  default: "d",
};

const PLACE_ICON_CODE_TO_CATEGORY: Record<string, PlaceIconCategory> =
  Object.entries(PLACE_ICON_CATEGORY_CODES).reduce(
    (acc, [category, code]) => ({
      ...acc,
      [code]: category as PlaceIconCategory,
    }),
    {} as Record<string, PlaceIconCategory>
  );

const PLACE_ICON_CATEGORY_TO_PLACE_TYPES: Record<PlaceIconCategory, string[]> = {
  city: ["locality"],
  airport: ["airport"],
  hotel: ["lodging"],
  food: ["restaurant"],
  museum: ["museum"],
  park: ["park"],
  shopping: ["shopping_mall"],
  transit: ["transit_station"],
  fuel: ["gas_station"],
  parking: ["parking"],
  hospital: ["hospital"],
  education: ["school"],
  attraction: ["tourist_attraction"],
  default: [],
};

/** Google Places type → icon category (first meaningful match wins). */
const GOOGLE_TYPE_TO_ICON_CATEGORY: Record<string, PlaceIconCategory> = {
  airport: "airport",
  lodging: "hotel",
  restaurant: "food",
  cafe: "food",
  bar: "food",
  bakery: "food",
  meal_takeaway: "food",
  meal_delivery: "food",
  museum: "museum",
  art_gallery: "museum",
  park: "park",
  campground: "park",
  rv_park: "park",
  shopping_mall: "shopping",
  department_store: "shopping",
  clothing_store: "shopping",
  store: "shopping",
  supermarket: "shopping",
  train_station: "transit",
  subway_station: "transit",
  transit_station: "transit",
  bus_station: "transit",
  taxi_stand: "transit",
  gas_station: "fuel",
  parking: "parking",
  hospital: "hospital",
  school: "education",
  university: "education",
  locality: "city",
  postal_town: "city",
  administrative_area_level_1: "city",
  administrative_area_level_2: "city",
  country: "city",
  neighborhood: "city",
  tourist_attraction: "attraction",
  stadium: "attraction",
  movie_theater: "attraction",
  amusement_park: "attraction",
  zoo: "attraction",
  aquarium: "attraction",
  gym: "attraction",
  spa: "attraction",
  night_club: "attraction",
  casino: "attraction",
};

const PLACE_TYPE_NOISE = new Set([
  "geocode",
  "political",
  "establishment",
  "point_of_interest",
  "premise",
  "subpremise",
  "plus_code",
  "route",
  "street_address",
]);

export const resolvePlaceIconCategory = (
  placeTypes?: string[]
): PlaceIconCategory => {
  if (!placeTypes?.length) {
    return "default";
  }

  for (const type of placeTypes) {
    if (PLACE_TYPE_NOISE.has(type)) {
      continue;
    }
    const category = GOOGLE_TYPE_TO_ICON_CATEGORY[type];
    if (category) {
      return category;
    }
  }

  return "default";
};

/** Public URL for the place-category map pin SVG. */
export const getPlaceTypeMarkerIconUrl = (
  placeTypes?: string[]
): string => {
  const category = resolvePlaceIconCategory(placeTypes);
  return `${MAP_MARKERS_SVG_ICONS_BASE}places/${category}.svg`;
};

export const encodePlaceIconCategory = (placeTypes?: string[]): string => {
  const category = resolvePlaceIconCategory(placeTypes);
  return PLACE_ICON_CATEGORY_CODES[category];
};

export const decodePlaceIconCategory = (
  code?: string
): PlaceIconCategory => {
  if (!code) {
    return "default";
  }

  return PLACE_ICON_CODE_TO_CATEGORY[code] ?? "default";
};

export const getPlaceTypesForIconCode = (code?: string): string[] => {
  const category = decodePlaceIconCategory(code);
  return PLACE_ICON_CATEGORY_TO_PLACE_TYPES[category];
};
